import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Upload, CheckCircle2, Handshake, TrendingUp, ShieldCheck } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ConsentCheckboxes from "@/components/ConsentCheckboxes";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  name: "", phone: "", whatsapp: "", email: "", address: "", presentation: "", password: "", confirm: "",
};

export default function SellerApplyPage() {
  const [form, setForm] = useState(emptyForm);
  const [consent, setConsent] = useState({ accept_policy: false, accept_marketing: false });
  const settings = useSiteSettings();
  const [photo, setPhoto] = useState(null);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    if (form.password !== form.confirm) {
      toast.error("As palavras-passe não coincidem.");
      return;
    }
    setSending(true);
    try {
      let photo_url = "";
      if (photo) {
        const data = new FormData();
        data.append("file", photo);
        const res = await axios.post(`${API}/public/upload-image`, data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        photo_url = res.data.url;
      }
      await axios.post(`${API}/sellers/apply`, { ...form, photo_url });
      try {
        await axios.post(`${API}/privacy/consent`, {
          name: form.name, email: form.email,
          accept_policy: consent.accept_policy, accept_marketing: consent.accept_marketing,
          context: "candidatura_vendedor", policy_version: settings.privacy_policy_version || "1.0",
        });
      } catch { /* consent registado em segundo plano */ }
      setDone(true);
      toast.success("Candidatura submetida com sucesso!");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Não foi possível submeter. Tente novamente.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased">
      <Header />
      <main className="pt-20">
        {/* Hero */}
        <section className="bg-[#0B1E36] text-white py-20 sm:py-24">
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <p className="text-xs font-semibold tracking-[0.35em] uppercase text-slate-400 mb-4">Oportunidade</p>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-tight max-w-2xl" data-testid="seller-apply-heading">
              Seja Nosso Vendedor
            </h1>
            <p className="mt-5 text-base leading-relaxed text-slate-300 max-w-2xl">
              Junte-se à SOLUTION CAR PREMIUM como vendedor parceiro. Aceda ao nosso stock de viaturas,
              registe os seus clientes e ganhe a sua margem em cada venda — com total transparência.
            </p>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 grid sm:grid-cols-3 gap-6">
            {[
              { icon: Handshake, title: "Parceria Justa", text: "Defina a sua margem sobre o preço-base de cada viatura." },
              { icon: TrendingUp, title: "O Seu Rendimento", text: "Acompanhe clientes, negociações e comissões no seu painel." },
              { icon: ShieldCheck, title: "Apoio Total", text: "Stock, financiamento e processos geridos pela nossa equipa." },
            ].map(({ icon: Icon, title, text }) => (
              <div key={title} className="border border-slate-200 p-7">
                <span className="w-11 h-11 flex items-center justify-center bg-[#0072C6] text-white mb-5">
                  <Icon className="w-5 h-5" />
                </span>
                <h3 className="font-display text-lg text-[#0072C6]">{title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Form */}
        <section className="pb-20 sm:pb-24 bg-[#F8FAFC]">
          <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-16">
            {done ? (
              <div className="bg-white border border-slate-200 p-12 text-center" data-testid="seller-apply-success">
                <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-5" />
                <h2 className="font-display text-2xl text-[#0072C6] mb-3">Candidatura Recebida</h2>
                <p className="text-sm text-slate-600 max-w-md mx-auto">
                  Obrigado pelo seu interesse. A sua candidatura está em análise e será contactado em breve.
                  Após aprovação, poderá aceder ao seu painel de vendedor com o seu e-mail e palavra-passe.
                </p>
                <Link to="/entrar" className="inline-block mt-8 bg-[#0072C6] hover:bg-[#005FA3] text-white text-sm font-semibold px-8 py-3.5 transition-colors">
                  Ir para o Login
                </Link>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white border border-slate-200 p-6 sm:p-10" data-testid="seller-apply-form">
                <h2 className="font-display text-2xl text-[#0072C6] mb-8">Formulário de Candidatura</h2>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="s-name" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Nome completo *</label>
                    <input id="s-name" data-testid="seller-name" required minLength={2} value={form.name} onChange={set("name")}
                      className="w-full border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" placeholder="O seu nome completo" />
                  </div>
                  <div>
                    <label htmlFor="s-phone" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Telemóvel *</label>
                    <input id="s-phone" data-testid="seller-phone" required minLength={6} value={form.phone} onChange={set("phone")}
                      className="w-full border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" placeholder="+351 912 345 678" />
                  </div>
                  <div>
                    <label htmlFor="s-whatsapp" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">WhatsApp</label>
                    <input id="s-whatsapp" data-testid="seller-whatsapp" value={form.whatsapp} onChange={set("whatsapp")}
                      className="w-full border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" placeholder="+351 912 345 678" />
                  </div>
                  <div>
                    <label htmlFor="s-email" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">E-mail *</label>
                    <input id="s-email" type="email" data-testid="seller-email" required value={form.email} onChange={set("email")}
                      className="w-full border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" placeholder="voce@email.com" />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="s-address" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Morada</label>
                    <input id="s-address" data-testid="seller-address" value={form.address} onChange={set("address")}
                      className="w-full border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" placeholder="Rua, nº, código postal, cidade" />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="s-presentation" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Breve apresentação</label>
                    <textarea id="s-presentation" data-testid="seller-presentation" rows={4} value={form.presentation} onChange={set("presentation")}
                      className="w-full border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6] resize-y"
                      placeholder="Fale-nos da sua experiência e motivação…" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Fotografia de perfil</label>
                    <label className="inline-flex items-center gap-2 border border-slate-300 px-4 py-2.5 text-sm text-slate-600 cursor-pointer hover:border-[#0072C6] hover:text-[#0072C6] transition-colors">
                      <Upload className="w-4 h-4" />
                      {photo ? photo.name : "Carregar fotografia"}
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => setPhoto((e.target.files || [])[0] || null)} data-testid="seller-photo" />
                    </label>
                  </div>
                  <div>
                    <label htmlFor="s-password" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Palavra-passe *</label>
                    <input id="s-password" type="password" data-testid="seller-password" required minLength={8} value={form.password} onChange={set("password")}
                      className="w-full border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" placeholder="Mínimo 8 caracteres" />
                  </div>
                  <div>
                    <label htmlFor="s-confirm" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Confirmar palavra-passe *</label>
                    <input id="s-confirm" type="password" data-testid="seller-confirm" required minLength={8} value={form.confirm} onChange={set("confirm")}
                      className="w-full border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]" placeholder="Repita a palavra-passe" />
                  </div>
                </div>
                <div className="sm:col-span-2 mt-2">
                  <ConsentCheckboxes consent={consent} onChange={setConsent} idPrefix="seller" />
                </div>
                <button type="submit" disabled={sending} data-testid="seller-submit"
                  className="mt-8 inline-flex items-center gap-2 bg-[#FF6A00] hover:bg-[#e65f00] disabled:opacity-60 text-white text-sm font-semibold px-8 py-3.5 transition-colors">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Enviar Candidatura
                </button>
              </form>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
